/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * MfaSetupRequiredPage Component
 * ============================================================================
 *
 * MFA setup page for users with enforced MFA during login.
 * Multi-step wizard: Verify Identity -> Choose Method -> Setup -> Complete
 *
 * ============================================================================
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle, ArrowLeft, Mail, Smartphone, Check, Copy, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { CodeInput } from '@/components/ui/CodeInput';
import { Button } from '@/components/ui/Button';
import {
  useVerifyIdentityForMfaSetup,
  useInitTotpForMfaSetup,
  useCompleteMfaSetup,
  useSendEmailOtp,
} from '@/hooks/api/useAuth';
import { useDeviceFingerprint, getDeviceFingerprintSync } from '@/hooks/useDeviceFingerprint';
import { isApiError } from '@/services/api/errors';
import { MfaMethod, MfaSetupResponse } from '@/types/auth.types';

type SetupStep = 'verify-identity' | 'choose-method' | 'setup-totp' | 'setup-email' | 'complete';

interface LocationState {
  mfaToken: string;
  userEmail: string;
  availableSetupMethods?: MfaMethod[];
}

export function MfaSetupRequiredPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { fingerprint } = useDeviceFingerprint();

  // Mutations
  const verifyIdentityMutation = useVerifyIdentityForMfaSetup();
  const initTotpMutation = useInitTotpForMfaSetup();
  const completeMfaSetupMutation = useCompleteMfaSetup();
  const sendEmailOtpMutation = useSendEmailOtp();

  // State
  const [step, setStep] = useState<SetupStep>('verify-identity');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<MfaMethod | null>(null);
  const [totpSetupData, setTotpSetupData] = useState<MfaSetupResponse | null>(null);
  const [resendCooldown, setResendCooldown] = useState(60); // Start with cooldown since code was sent
  const [resendSuccess, setResendSuccess] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Available MFA methods from login response
  const availableMethods = state?.availableSetupMethods || ['TOTP', 'EMAIL_OTP'];

  // Redirect if no MFA token
  useEffect(() => {
    if (!state?.mfaToken) {
      navigate('/auth/login');
    }
  }, [state, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle identity verification with Email OTP
  const handleVerifyIdentity = async (verificationCode: string) => {
    if (!state?.mfaToken) return;

    setError(null);

    verifyIdentityMutation.mutate(
      {
        mfaToken: state.mfaToken,
        emailOtpCode: verificationCode,
      },
      {
        onSuccess: () => {
          setCode('');
          setStep('choose-method');
        },
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || t('auth.mfa.invalidCode'));
          } else {
            setError(t('auth.mfa.invalidCode'));
          }
          setCode('');
        },
      }
    );
  };

  // Handle method selection
  const handleMethodSelect = async (method: MfaMethod) => {
    if (!state?.mfaToken) return;

    setSelectedMethod(method);
    setError(null);
    setCode('');

    if (method === 'TOTP') {
      // Initialize TOTP setup
      initTotpMutation.mutate(
        { mfaToken: state.mfaToken },
        {
          onSuccess: (data) => {
            setTotpSetupData(data);
            setStep('setup-totp');
          },
          onError: (err) => {
            if (isApiError(err)) {
              setError(err.message || t('notifications.error'));
            } else {
              setError(t('notifications.error'));
            }
          },
        }
      );
    } else if (method === 'EMAIL_OTP') {
      // For Email OTP, send a new code and go to setup step
      // Use isSetup: true to skip cooldown for initial setup
      sendEmailOtpMutation.mutate({ mfaToken: state.mfaToken, isSetup: true }, {
        onSuccess: () => {
          setResendCooldown(60);
          setStep('setup-email');
        },
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || t('notifications.error'));
          } else {
            setError(t('notifications.error'));
          }
        },
      });
    }
  };

  // Handle MFA setup completion
  const handleComplete = async (verificationCode: string) => {
    if (!state?.mfaToken || !selectedMethod) return;

    setError(null);

    const deviceFp = fingerprint ?? getDeviceFingerprintSync();

    completeMfaSetupMutation.mutate(
      {
        mfaToken: state.mfaToken,
        method: selectedMethod,
        verificationCode: verificationCode,
        trustDevice: false,
        deviceFingerprint: deviceFp.hash,
        browser: deviceFp.info.browser,
        browserVersion: deviceFp.info.browserVersion,
        os: deviceFp.info.os,
        osVersion: deviceFp.info.osVersion,
      },
      {
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || t('auth.mfa.invalidCode'));
          } else {
            setError(t('auth.mfa.invalidCode'));
          }
          setCode('');
        },
      }
    );
  };

  // Resend email OTP (respects cooldown timer)
  const handleResendEmailOtp = () => {
    if (!state?.mfaToken || sendEmailOtpMutation.isPending || resendCooldown > 0) return;

    setError(null);
    setResendSuccess(false);

    sendEmailOtpMutation.mutate({ mfaToken: state.mfaToken, isSetup: false }, {
      onSuccess: () => {
        setResendCooldown(60);
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      },
      onError: (err) => {
        if (isApiError(err)) {
          setError(err.message || t('notifications.error'));
        } else {
          setError(t('notifications.error'));
        }
      },
    });
  };

  // Copy secret to clipboard
  const handleCopySecret = async () => {
    if (!totpSetupData?.secret) return;
    try {
      await navigator.clipboard.writeText(totpSetupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  // Copy recovery codes to clipboard
  const handleCopyRecoveryCodes = async () => {
    if (!totpSetupData?.recoveryCodes) return;
    try {
      await navigator.clipboard.writeText(totpSetupData.recoveryCodes.join('\n'));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  const handleCodeComplete = (completeCode: string) => {
    setCode(completeCode);
    if (step === 'verify-identity') {
      handleVerifyIdentity(completeCode);
    } else if (step === 'setup-totp' || step === 'setup-email') {
      handleComplete(completeCode);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'verify-identity':
        return t('mfaSetup.required.verifyIdentity');
      case 'choose-method':
        return t('mfaSetup.required.chooseMethod');
      case 'setup-totp':
        return t('mfaSetup.required.setupTotp');
      case 'setup-email':
        return t('mfaSetup.required.confirmEmail');
      default:
        return t('mfaSetup.required.title');
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case 'verify-identity':
        return t('mfaSetup.required.verifyDescription').replace('{{email}}', state?.userEmail || '');
      case 'choose-method':
        return t('mfaSetup.required.subtitle');
      case 'setup-totp':
        return t('mfaSetup.required.scanQrCode');
      case 'setup-email':
        return t('mfaSetup.required.confirmEmailDesc');
      default:
        return '';
    }
  };

  const isLoading =
    verifyIdentityMutation.isPending ||
    initTotpMutation.isPending ||
    completeMfaSetupMutation.isPending ||
    sendEmailOtpMutation.isPending;

  return (
    <AuthLayout title={getStepTitle()} subtitle={getStepSubtitle()} icon={<Shield size={28} />}>
      <div className="mfa-setup">
        {/* Progress indicator */}
        <div className="mfa-setup-progress">
          <div className={`progress-step ${step !== 'verify-identity' ? 'completed' : 'active'}`}>
            <div className="step-dot">{step !== 'verify-identity' ? <Check size={12} /> : '1'}</div>
            <span>{t('mfaSetup.required.stepVerify')}</span>
          </div>
          <div className="progress-line" />
          <div
            className={`progress-step ${
              step === 'setup-totp' || step === 'setup-email' || step === 'complete'
                ? 'completed'
                : step === 'choose-method'
                ? 'active'
                : ''
            }`}
          >
            <div className="step-dot">
              {step === 'setup-totp' || step === 'setup-email' || step === 'complete' ? (
                <Check size={12} />
              ) : (
                '2'
              )}
            </div>
            <span>{t('mfaSetup.required.stepChoose')}</span>
          </div>
          <div className="progress-line" />
          <div className={`progress-step ${step === 'complete' ? 'completed' : step.startsWith('setup') ? 'active' : ''}`}>
            <div className="step-dot">{step === 'complete' ? <Check size={12} /> : '3'}</div>
            <span>{t('mfaSetup.required.stepSetup')}</span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mfa-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Verify Identity */}
        {step === 'verify-identity' && (
          <div className="mfa-step-content">
            <div className="mfa-code-section">
              <CodeInput
                length={6}
                onComplete={handleCodeComplete}
                onChange={setCode}
                disabled={isLoading}
                error={!!error}
              />
            </div>

            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              loading={verifyIdentityMutation.isPending}
              loadingText={t('auth.mfa.verifying')}
              disabled={code.length !== 6}
              onClick={() => handleVerifyIdentity(code)}
              glow
            >
              {t('mfaSetup.required.verifyButton')}
            </Button>

            <div className="mfa-resend-section">
              {resendSuccess && (
                <div className="mfa-resend-success">
                  <Mail size={16} />
                  <span>{t('auth.mfa.codeSent')}</span>
                </div>
              )}
              <div className="mfa-resend-container">
                <span className="mfa-resend-text">{t('auth.mfa.didntReceive')}</span>
                <button
                  type="button"
                  className={`mfa-resend-btn ${resendCooldown > 0 ? 'mfa-resend-btn-disabled' : ''}`}
                  onClick={handleResendEmailOtp}
                  disabled={sendEmailOtpMutation.isPending || resendCooldown > 0}
                >
                  <Mail size={14} />
                  {sendEmailOtpMutation.isPending
                    ? t('auth.mfa.sending')
                    : resendCooldown > 0
                    ? t('auth.mfa.waitSeconds').replace('{{seconds}}', String(resendCooldown))
                    : t('auth.mfa.resendCode')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Choose Method */}
        {step === 'choose-method' && (
          <div className="mfa-step-content">
            <div className="mfa-method-cards">
              {availableMethods.includes('TOTP') && (
                <button
                  type="button"
                  className="mfa-method-card"
                  onClick={() => handleMethodSelect('TOTP')}
                  disabled={isLoading}
                >
                  <div className="method-icon">
                    <Smartphone size={24} />
                  </div>
                  <div className="method-info">
                    <h4>{t('mfaSetup.required.methodTotp')}</h4>
                    <p>{t('mfaSetup.required.methodTotpDesc')}</p>
                  </div>
                  <div className="method-arrow">&rarr;</div>
                </button>
              )}

              {availableMethods.includes('EMAIL_OTP') && (
                <button
                  type="button"
                  className="mfa-method-card"
                  onClick={() => handleMethodSelect('EMAIL_OTP')}
                  disabled={isLoading}
                >
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div className="method-info">
                    <h4>{t('mfaSetup.required.methodEmail')}</h4>
                    <p>{t('mfaSetup.required.methodEmailDesc')}</p>
                  </div>
                  <div className="method-arrow">&rarr;</div>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3a: Setup TOTP */}
        {step === 'setup-totp' && totpSetupData && (
          <div className="mfa-step-content">
            {/* QR Code */}
            <div className="totp-qr-section">
              <div className="qr-code-container">
                <img
                  src={`data:image/png;base64,${totpSetupData.qrCodeBase64}`}
                  alt="QR Code"
                  className="qr-code-image"
                />
              </div>
              <div className="secret-key-container">
                <span className="secret-label">{t('mfaSetup.required.manualEntry')}</span>
                <div className="secret-key">
                  <code>{totpSetupData.secret}</code>
                  <button type="button" className="copy-btn" onClick={handleCopySecret}>
                    {copiedSecret ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Recovery Codes */}
            {totpSetupData.recoveryCodes && totpSetupData.recoveryCodes.length > 0 && (
              <div className="recovery-codes-section">
                <div className="recovery-codes-header">
                  <h4>{t('mfaSetup.required.recoveryCodes')}</h4>
                  <button type="button" className="copy-all-btn" onClick={handleCopyRecoveryCodes}>
                    {copiedCodes ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    {copiedCodes ? t('common.copied') : t('common.copyAll')}
                  </button>
                </div>
                <p className="recovery-codes-warning">{t('mfaSetup.required.recoveryCodesWarning')}</p>
                <div className="recovery-codes-grid">
                  {totpSetupData.recoveryCodes.map((code, index) => (
                    <code key={index} className="recovery-code">
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {/* Verification Code Input */}
            <div className="totp-verify-section">
              <label className="verify-label">{t('mfaSetup.required.enterCode')}</label>
              <div className="mfa-code-section">
                <CodeInput
                  length={6}
                  onComplete={handleCodeComplete}
                  onChange={setCode}
                  disabled={isLoading}
                  error={!!error}
                />
              </div>

              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                loading={completeMfaSetupMutation.isPending}
                loadingText={t('mfaSetup.required.completing')}
                disabled={code.length !== 6}
                onClick={() => handleComplete(code)}
                glow
              >
                {t('mfaSetup.required.completeSetup')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3b: Setup Email OTP */}
        {step === 'setup-email' && (
          <div className="mfa-step-content">
            <div className="email-setup-info">
              <Mail size={48} className="email-icon" />
              <p>{t('mfaSetup.required.emailSetupDesc')}</p>
            </div>

            <div className="mfa-code-section">
              <CodeInput
                length={6}
                onComplete={handleCodeComplete}
                onChange={setCode}
                disabled={isLoading}
                error={!!error}
              />
            </div>

            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              loading={completeMfaSetupMutation.isPending}
              loadingText={t('mfaSetup.required.completing')}
              disabled={code.length !== 6}
              onClick={() => handleComplete(code)}
              glow
            >
              {t('mfaSetup.required.completeSetup')}
            </Button>

            <div className="mfa-resend-section">
              {resendSuccess && (
                <div className="mfa-resend-success">
                  <Mail size={16} />
                  <span>{t('auth.mfa.codeSent')}</span>
                </div>
              )}
              <div className="mfa-resend-container">
                <span className="mfa-resend-text">{t('auth.mfa.didntReceive')}</span>
                <button
                  type="button"
                  className={`mfa-resend-btn ${resendCooldown > 0 ? 'mfa-resend-btn-disabled' : ''}`}
                  onClick={handleResendEmailOtp}
                  disabled={sendEmailOtpMutation.isPending || resendCooldown > 0}
                >
                  <Mail size={14} />
                  {sendEmailOtpMutation.isPending
                    ? t('auth.mfa.sending')
                    : resendCooldown > 0
                    ? t('auth.mfa.waitSeconds').replace('{{seconds}}', String(resendCooldown))
                    : t('auth.mfa.resendCode')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back button */}
        <div className="mfa-footer">
          {step === 'choose-method' && (
            <button type="button" className="mfa-back-link" onClick={() => navigate('/auth/login')}>
              <ArrowLeft size={16} />
              {t('auth.mfa.backToLogin')}
            </button>
          )}
          {(step === 'setup-totp' || step === 'setup-email') && (
            <button type="button" className="mfa-back-link" onClick={() => setStep('choose-method')}>
              <ArrowLeft size={16} />
              {t('mfaSetup.required.backToMethods')}
            </button>
          )}
          {step === 'verify-identity' && (
            <button type="button" className="mfa-back-link" onClick={() => navigate('/auth/login')}>
              <ArrowLeft size={16} />
              {t('auth.mfa.backToLogin')}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .mfa-setup {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .mfa-setup-progress {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          opacity: 0.5;
        }

        .progress-step.active,
        .progress-step.completed {
          opacity: 1;
        }

        .step-dot {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-muted);
          transition: all 0.3s ease;
        }

        .progress-step.active .step-dot {
          background: rgba(163, 22, 33, 0.2);
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }

        .progress-step.completed .step-dot {
          background: var(--color-brand-primary);
          border-color: var(--color-brand-primary);
          color: white;
        }

        .progress-step span {
          font-size: 11px;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .progress-step.active span,
        .progress-step.completed span {
          color: var(--color-text-primary);
        }

        .progress-line {
          width: 32px;
          height: 2px;
          background: rgba(255, 255, 255, 0.1);
          margin-bottom: 20px;
        }

        [data-theme="light"] .progress-line {
          background: rgba(0, 0, 0, 0.1);
        }

        [data-theme="light"] .step-dot {
          background: rgba(0, 0, 0, 0.05);
          border-color: rgba(0, 0, 0, 0.1);
        }

        [data-theme="light"] .progress-step.active .step-dot {
          background: rgba(26, 95, 122, 0.1);
          border-color: var(--color-brand-primary);
        }

        .mfa-error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-lg);
          color: var(--color-error);
          font-size: var(--text-sm);
          animation: shake 0.3s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        .mfa-step-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .mfa-code-section {
          padding: 8px 0;
        }

        /* Method Cards */
        .mfa-method-cards {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mfa-method-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .mfa-method-card:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          border-color: var(--color-brand-primary);
          transform: translateY(-2px);
        }

        .mfa-method-card:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        [data-theme="light"] .mfa-method-card {
          background: rgba(0, 0, 0, 0.02);
          border-color: rgba(0, 0, 0, 0.1);
        }

        [data-theme="light"] .mfa-method-card:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.04);
        }

        .method-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(163, 22, 33, 0.15);
          border-radius: var(--radius-md);
          color: var(--color-brand-primary);
        }

        [data-theme="light"] .method-icon {
          background: rgba(26, 95, 122, 0.1);
        }

        .method-info {
          flex: 1;
        }

        .method-info h4 {
          margin: 0;
          font-size: var(--text-base);
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .method-info p {
          margin: 4px 0 0;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
        }

        .method-arrow {
          font-size: 20px;
          color: var(--color-text-muted);
        }

        /* TOTP Setup */
        .totp-qr-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
        }

        [data-theme="light"] .totp-qr-section {
          background: rgba(0, 0, 0, 0.02);
          border-color: rgba(0, 0, 0, 0.08);
        }

        .qr-code-container {
          padding: 12px;
          background: white;
          border-radius: var(--radius-md);
        }

        .qr-code-image {
          width: 160px;
          height: 160px;
        }

        .secret-key-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          width: 100%;
        }

        .secret-label {
          font-size: var(--text-xs);
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .secret-key {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: var(--radius-md);
        }

        [data-theme="light"] .secret-key {
          background: rgba(0, 0, 0, 0.05);
        }

        .secret-key code {
          font-family: var(--font-mono);
          font-size: var(--text-sm);
          color: var(--color-text-primary);
          letter-spacing: 1px;
        }

        .copy-btn {
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--color-text-muted);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .copy-btn:hover {
          color: var(--color-brand-primary);
        }

        /* Recovery Codes */
        .recovery-codes-section {
          padding: 16px;
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: var(--radius-lg);
        }

        .recovery-codes-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .recovery-codes-header h4 {
          margin: 0;
          font-size: var(--text-sm);
          font-weight: 600;
          color: #D97706;
        }

        .copy-all-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: transparent;
          border: 1px solid rgba(255, 193, 7, 0.4);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          color: #D97706;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .copy-all-btn:hover {
          background: rgba(255, 193, 7, 0.15);
        }

        .recovery-codes-warning {
          margin: 0 0 12px;
          font-size: var(--text-xs);
          color: var(--color-text-muted);
        }

        .recovery-codes-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }

        .recovery-code {
          padding: 6px 10px;
          background: rgba(0, 0, 0, 0.15);
          border-radius: var(--radius-sm);
          font-family: var(--font-mono);
          font-size: var(--text-xs);
          color: var(--color-text-primary);
          text-align: center;
        }

        [data-theme="light"] .recovery-code {
          background: rgba(0, 0, 0, 0.05);
        }

        .totp-verify-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .verify-label {
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--color-text-primary);
          text-align: center;
        }

        /* Email Setup */
        .email-setup-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 24px;
          text-align: center;
        }

        .email-icon {
          color: var(--color-brand-primary);
          opacity: 0.8;
        }

        .email-setup-info p {
          margin: 0;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
        }

        /* Resend Section */
        .mfa-resend-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mfa-resend-success {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.4);
          border-radius: var(--radius-lg);
          color: #10B981;
          font-size: var(--text-sm);
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mfa-resend-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          font-size: var(--text-sm);
        }

        .mfa-resend-text {
          color: var(--color-text-muted);
        }

        .mfa-resend-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(163, 22, 33, 0.15);
          border: 1px solid rgba(163, 22, 33, 0.4);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--color-brand-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mfa-resend-btn:hover:not(:disabled):not(.mfa-resend-btn-disabled) {
          background: rgba(163, 22, 33, 0.25);
          border-color: rgba(163, 22, 33, 0.6);
          transform: translateY(-1px);
        }

        .mfa-resend-btn:disabled,
        .mfa-resend-btn-disabled {
          background: rgba(100, 116, 139, 0.1);
          border-color: rgba(100, 116, 139, 0.2);
          color: var(--color-text-muted);
          cursor: not-allowed;
          transform: none;
        }

        [data-theme="light"] .mfa-resend-btn {
          background: rgba(26, 95, 122, 0.1);
          border-color: rgba(26, 95, 122, 0.3);
        }

        [data-theme="light"] .mfa-resend-btn:hover:not(:disabled):not(.mfa-resend-btn-disabled) {
          background: rgba(26, 95, 122, 0.15);
          border-color: rgba(26, 95, 122, 0.5);
        }

        /* Footer */
        .mfa-footer {
          display: flex;
          justify-content: center;
          padding-top: 8px;
        }

        .mfa-back-link {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: none;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .mfa-back-link:hover {
          color: var(--color-text-primary);
        }

        @media (max-width: 640px) {
          .mfa-setup {
            gap: 1.25rem;
          }
          .mfa-setup-progress {
            gap: 4px;
          }
          .progress-step span {
            font-size: 10px;
          }
          .progress-line {
            width: 20px;
          }
          .mfa-method-card {
            padding: 12px 14px;
            gap: 12px;
          }
          .totp-qr-section {
            padding: 1rem;
          }
          .recovery-codes-grid {
            grid-template-columns: 1fr;
          }
          .mfa-resend-container {
            flex-direction: column;
            gap: 8px;
            align-items: center;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .mfa-error { animation: none !important; }
          .mfa-resend-success { animation: none !important; }
        }
      `}</style>
    </AuthLayout>
  );
}

export default MfaSetupRequiredPage;
